/**
 * Page-Specific Error Boundary Component
 * Provides specialized error boundaries for different page types
 */

import React from 'react';
import type { ReactNode, ErrorInfo } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { Home, Refresh, Map, List, Settings } from '@mui/icons-material';
import ErrorBoundary from './ErrorBoundary';

interface PageErrorBoundaryProps {
  children: ReactNode;
  pageName: string;
  pageType: 'map' | 'routes' | 'settings' | 'auth' | 'static' | 'general';
  fallbackPath?: string;
}

const getPageIcon = (pageType: string) => {
  switch (pageType) {
    case 'map': return <Map fontSize="large" />;
    case 'routes': return <List fontSize="large" />;
    case 'settings': return <Settings fontSize="large" />;
    default: return <Home fontSize="large" />;
  }
};

const getPageSpecificMessage = (pageName: string, pageType: string) => {
  switch (pageType) {
    case 'map':
      return 'The map interface encountered an error. You may want to try refreshing the page or returning to the main map view.';
    case 'routes':
      return 'There was an error loading your routes. Your routes are safe - this is just a display issue.';
    case 'settings':
      return 'The settings page encountered an error. Your settings are preserved and you can try again.';
    case 'auth':
      return 'There was an error with the authentication process. Please try signing in again.';
    case 'static':
      return `The ${pageName} page encountered an error while loading.`;
    default:
      return `The ${pageName} page encountered an error and couldn't load properly.`;
  }
};

const getPageSpecificActions = (pageType: string, fallbackPath?: string) => {
  const actions = [
    {
      label: 'Try Again',
      icon: <Refresh />,
      action: 'retry',
      variant: 'contained' as const,
      color: 'primary' as const
    },
    {
      label: 'Refresh Page',
      icon: <Refresh />,
      action: 'refresh',
      variant: 'outlined' as const,
      color: 'primary' as const
    }
  ];

  // Add page-specific actions
  switch (pageType) {
    case 'routes':
      actions.push({
        label: 'Go to Map',
        icon: <Map />,
        action: 'navigate-home',
        variant: 'outlined' as const,
        color: 'primary' as const
      });
      break;
    case 'settings':
      actions.push({
        label: 'Go to Map',
        icon: <Map />,
        action: 'navigate-home',
        variant: 'outlined' as const,
        color: 'primary' as const
      });
      break;
    case 'auth':
      actions.push({
        label: 'Go Home',
        icon: <Home />,
        action: 'navigate-home',
        variant: 'outlined' as const,
        color: 'primary' as const
      });
      break;
    default:
      if (fallbackPath) {
        actions.push({
          label: 'Go Back',
          icon: <Home />,
          action: 'navigate-back',
          variant: 'outlined' as const,
          color: 'primary' as const
        });
      } else {
        actions.push({
          label: 'Go Home',
          icon: <Home />,
          action: 'navigate-home',
          variant: 'outlined' as const,
          color: 'primary' as const
        });
      }
  }

  return actions;
};

export const PageErrorBoundary: React.FC<PageErrorBoundaryProps> = ({
  children,
  pageName,
  pageType,
  fallbackPath
}) => {
  const handleError = (error: Error, errorInfo: ErrorInfo) => {
    console.error(`${pageName} page error:`, error, errorInfo);
  };

  const actions = getPageSpecificActions(pageType, fallbackPath);

  const handleActionClick = (action: string) => {
    switch (action) {
      case 'navigate-home':
        window.location.href = '/';
        break;
      case 'navigate-back':
        if (fallbackPath) {
          window.location.href = fallbackPath;
        } else {
          window.location.href = '/';
        }
        break;
      // 'retry' and 'refresh' are handled by the ErrorBoundary component
    }
  };

  const fallbackUI = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        padding: 3,
        textAlign: 'center',
        maxWidth: '600px',
        margin: '0 auto'
      }}
    >
      <Box sx={{ mb: 3, color: 'text.secondary' }}>
        {getPageIcon(pageType)}
      </Box>
      
      <Typography variant="h4" component="h1" gutterBottom color="error">
        {pageName} Error
      </Typography>
      
      <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
        {getPageSpecificMessage(pageName, pageType)}
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
        {actions.map((action, index) => (
          <Button
            key={index}
            variant={action.variant}
            color={action.color}
            startIcon={action.icon}
            onClick={() => handleActionClick(action.action)}
            data-action={action.action}
          >
            {action.label}
          </Button>
        ))}
      </Box>
    </Box>
  );

  return (
    <ErrorBoundary
      fallback={fallbackUI}
      onError={handleError}
      showDetails={import.meta.env.DEV}
    >
      {children}
    </ErrorBoundary>
  );
};

export default PageErrorBoundary;
